import { API_TAGS } from "../../helpers/constants";
import {
  IAudioRecording,
  ICase,
  IGetCasesRequestProps,
  IGetCasesResponseProps,
  IPostCaseCreateRequestProps,
} from "../../types/Case";
import { api } from "../api";

export const caseApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCases: builder.query<IGetCasesResponseProps, IGetCasesRequestProps>({
      query: (params) => ({ url: "cases/all", params }),
      providesTags: () => [API_TAGS.CASE],
    }),
    getCase: builder.query<ICase, string | undefined | number>({
      query: (id: string) => ({ url: `cases/${id}` }),
      providesTags: () => [API_TAGS.CASE],
      transformResponse: async (response: ICase): Promise<ICase> => {
        return {
          ...response,
          patientFullName:
            response.patientFirstName + " " + response.patientLastName,
        };
      },
    }),
    postCaseCreate: builder.mutation<
      { id: number },
      IPostCaseCreateRequestProps
    >({
      query: (credentials) => ({
        url: "cases",
        method: "POST",
        body: credentials,
      }),
      extraOptions: {},
      invalidatesTags: [API_TAGS.CASE],
    }),
    putCaseUpdate: builder.mutation<
      unknown,
      { id: string } & Omit<IPostCaseCreateRequestProps, "caseType">
    >({
      query: ({ id, ...credentials }) => ({
        url: `cases/${id}`,
        method: "PUT",
        body: credentials,
      }),
      extraOptions: {},
      invalidatesTags: [API_TAGS.CASE],
    }),
    putCaseArchive: builder.mutation<unknown, { id: string }>({
      query: ({ id, ...credentials }) => ({
        url: `cases/archive/${id}`,
        method: "PUT",
        body: credentials,
      }),
      extraOptions: {},
      invalidatesTags: [API_TAGS.CASE],
    }),
    putCaseFavorite: builder.mutation<unknown, { id: string }>({
      query: ({ id, ...credentials }) => ({
        url: `cases/favorite/${id}`,
        method: "PUT",
        body: credentials,
      }),
      extraOptions: {},
      invalidatesTags: [API_TAGS.CASE],
    }),
    deleteCase: builder.mutation<null, number>({
      query: (id) => ({
        url: `cases/${id}`,
        method: "DELETE",
      }),
      extraOptions: {},
      invalidatesTags: [API_TAGS.CASE],
    }),
    postCaseComplaint: builder.mutation<null, { id: number; text: string }>({
      query: ({ id, text }) => ({
        url: `cases/${id}/complaint?text=${text}`,
        method: "POST",
      }),
      extraOptions: {},
      invalidatesTags: [],
    }),
    postCaseTreatmentPlan: builder.mutation<null, { id: number; text: string }>(
      {
        query: ({ id, text }) => ({
          url: `cases/${id}/treatment-plan?text=${text}`,
          method: "POST",
        }),
        extraOptions: {},
        invalidatesTags: [],
      }
    ),
    // Audio Recordings (new API with recordingId)
    createAudioRecording: builder.mutation<{ id: number }, { caseId: number }>({
      query: ({ caseId }) => ({
        url: `cases/${caseId}/audio-recordings`,
        method: "POST",
      }),
      invalidatesTags: [API_TAGS.AUDIO_RECORDING],
    }),
    listAudioRecordings: builder.query<IAudioRecording[], string | number>({
      query: (caseId) => ({
        url: `cases/${caseId}/audio-recordings`,
      }),
      providesTags: () => [API_TAGS.AUDIO_RECORDING],
    }),
    postAudioRecordingChunk: builder.mutation<
      null,
      { caseId: number; recordingId: number; chunk: Blob; chunkIndex: number; filename?: string }
    >({
      query: ({ caseId, recordingId, chunk, chunkIndex, filename }) => {
        const formData = new FormData();
        formData.append("chunk", chunk, filename || `chunk_${chunkIndex}.wav`);
        formData.append("chunkIndex", chunkIndex.toString());
        return {
          url: `cases/${caseId}/audio-recordings/${recordingId}/chunk`,
          method: "POST",
          body: formData,
        };
      },
      extraOptions: {},
      invalidatesTags: [],
    }),
    finishAudioRecording: builder.mutation<null, { caseId: number; recordingId: number }>({
      query: ({ caseId, recordingId }) => ({
        url: `cases/${caseId}/audio-recordings/${recordingId}/finish`,
        method: "POST",
      }),
      extraOptions: {},
      invalidatesTags: [API_TAGS.AUDIO_RECORDING],
    }),
    getAudioRecordingAudio: builder.query<Blob, { caseId: string | number; recordingId: number }>({
      query: ({ caseId, recordingId }) => ({
        url: `cases/${caseId}/audio-recordings/${recordingId}/audio`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    getAudioRecordingTranscript: builder.query<string, { caseId: string | number; recordingId: number }>({
      query: ({ caseId, recordingId }) => ({
        url: `cases/${caseId}/audio-recordings/${recordingId}/transcript`,
        responseHandler: (response: Response) => response.text(),
      }),
    }),
    putAudioRecordingAnamnesis: builder.mutation<
      unknown,
      { caseId: string | number; recordingId: number; anamnesis: string }
    >({
      query: ({ caseId, recordingId, anamnesis }) => ({
        url: `cases/${caseId}/audio-recordings/${recordingId}/anamnesis`,
        method: "PUT",
        body: { anamnesis },
      }),
      invalidatesTags: [API_TAGS.AUDIO_RECORDING],
    }),
    putAudioRecordingComment: builder.mutation<
      unknown,
      { caseId: string | number; recordingId: number; audioComment: string }
    >({
      query: ({ caseId, recordingId, audioComment }) => ({
        url: `cases/${caseId}/audio-recordings/${recordingId}/comment`,
        method: "PUT",
        body: { audioComment },
      }),
      invalidatesTags: [API_TAGS.AUDIO_RECORDING],
    }),
    deleteAudioRecording: builder.mutation<null, { caseId: string | number; recordingId: number }>({
      query: ({ caseId, recordingId }) => ({
        url: `cases/${caseId}/audio-recordings/${recordingId}`,
        method: "DELETE",
      }),
      invalidatesTags: [API_TAGS.AUDIO_RECORDING],
    }),
    // Legacy audio endpoints (kept for backward compatibility)
    postCaseAudioChunk: builder.mutation<
      null,
      { id: number; chunk: Blob; chunkIndex: number; filename?: string }
    >({
      query: ({ id, chunk, chunkIndex, filename }) => {
        const formData = new FormData();
        formData.append("chunk", chunk, filename || `chunk_${chunkIndex}.wav`);
        formData.append("chunkIndex", chunkIndex.toString());
        return {
          url: `cases/${id}/audio/chunk`,
          method: "POST",
          body: formData,
        };
      },
      extraOptions: {},
      invalidatesTags: [],
    }),
    postCaseAudioFinish: builder.mutation<null, { id: number }>({
      query: ({ id }) => ({
        url: `cases/${id}/audio/finish`,
        method: "POST",
      }),
      extraOptions: {},
      invalidatesTags: [API_TAGS.CASE],
    }),
    getCaseAudio: builder.query<Blob, string | number>({
      query: (id) => ({
        url: `cases/${id}/audio`,
        responseHandler: (response) => response.blob(),
      }),
      providesTags: () => [API_TAGS.CASE],
    }),
    putCaseAudioAnamnesis: builder.mutation<
      unknown,
      { id: string; anamnesis: string }
    >({
      query: ({ id, anamnesis }) => ({
        url: `cases/${id}/audio/anamnesis`,
        method: "PUT",
        body: { anamnesis },
      }),
      invalidatesTags: [API_TAGS.CASE],
    }),
    putCaseAudioComment: builder.mutation<
      unknown,
      { id: string; audioComment: string }
    >({
      query: ({ id, audioComment }) => ({
        url: `cases/${id}/audio/comment`,
        method: "PUT",
        body: { audioComment },
      }),
      invalidatesTags: [API_TAGS.CASE],
    }),
  }),
});

export const {
  useGetCasesQuery,
  useGetCaseQuery,
  usePostCaseCreateMutation,
  usePutCaseUpdateMutation,
  usePutCaseArchiveMutation,
  usePutCaseFavoriteMutation,
  useDeleteCaseMutation,
  usePostCaseComplaintMutation,
  usePostCaseTreatmentPlanMutation,
  // New audio recording endpoints
  useCreateAudioRecordingMutation,
  useListAudioRecordingsQuery,
  usePostAudioRecordingChunkMutation,
  useFinishAudioRecordingMutation,
  useGetAudioRecordingAudioQuery,
  useLazyGetAudioRecordingAudioQuery,
  useGetAudioRecordingTranscriptQuery,
  useLazyGetAudioRecordingTranscriptQuery,
  usePutAudioRecordingAnamnesisMutation,
  usePutAudioRecordingCommentMutation,
  useDeleteAudioRecordingMutation,
  // Legacy audio endpoints
  usePostCaseAudioChunkMutation,
  usePostCaseAudioFinishMutation,
  useGetCaseAudioQuery,
  useLazyGetCaseAudioQuery,
  usePutCaseAudioAnamnesisMutation,
  usePutCaseAudioCommentMutation,
} = caseApi;
